import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
} from "react";
import { useStorage, type SupportedLocale } from "@repo/core";
import { uiLocaleTag } from "../i18n/localeMeta";

export type CurrencyCode =
    | "USD" | "EUR" | "GBP" | "CAD" | "AUD"
    | "MXN" | "JPY" | "CHF" | "BRL" | "INR";

export const CURRENCY_OPTIONS: {
  code: CurrencyCode;
  label: string;
  /** Fallback BCP-47 when UI locale is unavailable */
  locale: string;
}[] = [
  { code: "USD", label: "US Dollar ($)",            locale: "en-US" },
  { code: "EUR", label: "Euro (€)",                 locale: "de-DE" },
  { code: "GBP", label: "British Pound (£)",        locale: "en-GB" },
  { code: "CAD", label: "Canadian Dollar (CA$)",    locale: "en-CA" },
  { code: "AUD", label: "Australian Dollar (A$)",   locale: "en-AU" },
  { code: "MXN", label: "Mexican Peso (MX$)",       locale: "es-MX" },
  { code: "JPY", label: "Japanese Yen (¥)",         locale: "ja-JP" },
  { code: "CHF", label: "Swiss Franc (CHF)",        locale: "de-CH" },
  { code: "BRL", label: "Brazilian Real (R$)",      locale: "pt-BR" },
  { code: "INR", label: "Indian Rupee (₹)",         locale: "en-IN" },
];

export interface CurrencyContextValue {
  currency:    CurrencyCode;
  /** BCP-47 tag used for Intl formatting (follows UI language). */
  locale:      string;
  setCurrency: (code: CurrencyCode) => void;
  /** Format a number using the active UI locale + currency code */
  format:      (amount: number, compact?: boolean) => string;
  /** Just the symbol, e.g. "$", "€", "£" */
  symbol:      string;
}

const CurrencyContext = createContext<CurrencyContextValue>({
  currency:    "USD",
  locale:      "en-US",
  setCurrency: () => {},
  format:      (v) => `$${Math.abs(v).toFixed(2)}`,
  symbol:      "$",
});

export function useCurrency(): CurrencyContextValue {
  return useContext(CurrencyContext);
}

/**
 * Must sit inside StorageProvider so formatting follows the UI language
 * (symbol placement, decimal/thousands separators), not only the currency code.
 */
export function CurrencyProvider({ children }: { children: React.ReactNode }) {
  const { locale: uiLocale } = useStorage();
  const [currency, setCurrencyState] = useState<CurrencyCode>(() => {
    return (localStorage.getItem("budget-currency") as CurrencyCode) || "USD";
  });

  const setCurrency = useCallback((code: CurrencyCode) => {
    setCurrencyState(code);
    localStorage.setItem("budget-currency", code);
  }, []);

  const formatLocale = useMemo(
    () => uiLocaleTag(uiLocale as SupportedLocale),
    [uiLocale]
  );

  const format = useCallback(
    (amount: number, compact = false): string => {
      return new Intl.NumberFormat(formatLocale, {
        style:                 "currency",
        currency,
        notation:              compact ? "compact" : "standard",
        minimumFractionDigits: compact ? 0 : 2,
        maximumFractionDigits: compact ? 1 : 2,
      }).format(amount);
    },
    [currency, formatLocale]
  );

  const symbol = useMemo(() => {
    try {
      return (
        new Intl.NumberFormat(formatLocale, {
          style: "currency",
          currency,
        })
          .formatToParts(1)
          .find((p) => p.type === "currency")?.value ?? "$"
      );
    } catch {
      return "$";
    }
  }, [currency, formatLocale]);

  const value = useMemo<CurrencyContextValue>(
    () => ({
      currency,
      locale: formatLocale,
      setCurrency,
      format,
      symbol,
    }),
    [currency, formatLocale, setCurrency, format, symbol]
  );

  return (
    <CurrencyContext.Provider value={value}>
      {children}
    </CurrencyContext.Provider>
  );
}
